import React, { useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { EventPanelTab } from '../components/event-panel-tab';
import { TabBody } from '../../../panels/entity-panels/tabs/tab-body';
import { AttachmentsView, AttachmentsViewHandle } from '../../../attachments/attachments-view';
import { useOpenAttachmentsPanel } from '../../../attachments/use-open-attachments-panel';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import {
    MAX_NUM_OF_ATTACHMENTS_PER_EVENT,
    DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
} from '../../../attachments/constants';
import { AttachmentsHeaderActions } from '../../../attachments/attachments-header-actions';
import { AttachmentSortOptionValue } from '../../../attachments/types';
import { useIsClearedEvent } from '~/event-panel/event-panel-form/use-is-cleared-event';
import { useAttachmentsAbilities } from '~/attachments/hooks/use-attachments-abilities';

export const AttachmentsTab = observer(function AttachmentsTab(): React.ReactElement | null {
    const workspacePanel = useWorkspacePanel();
    const entityId = workspacePanel.linkedEntityId;
    const openAttachmentsPanel = useOpenAttachmentsPanel();
    const eventMetadataStore = useEventMetadataStore();
    const { canAddAttachments } = useAttachmentsAbilities('CAD_AGENCY_EVENT');
    const eventMetadata = eventMetadataStore.state.metadata;
    const [counts, setCounts] = useState({ attachmentCount: 0, mergedAttachmentCount: 0 });
    const [sortOption, setSortOption] = useState<AttachmentSortOptionValue>(
        DEFAULT_ATTACHMENT_SORT_OPTION_VALUE
    );
    const attachmentsViewRef = useRef<AttachmentsViewHandle>(null);
    const isMaxReached = counts.attachmentCount >= MAX_NUM_OF_ATTACHMENTS_PER_EVENT;
    const isClearedEvent = useIsClearedEvent();

    const handleAttachmentCountChange = useCallback(
        (attachmentCount: number, mergedAttachmentCount: number) => {
            setCounts({ attachmentCount, mergedAttachmentCount });
        },
        []
    );

    const handleSortChange = useCallback((newSortOption: AttachmentSortOptionValue) => {
        setSortOption(newSortOption);
    }, []);

    const handleUploadClick = () => {
        attachmentsViewRef.current?.openFileDialog();
    };

    if (!entityId) {
        return null;
    }

    const displayCount = counts.attachmentCount + counts.mergedAttachmentCount;
    const titleTrailingNode =
        canAddAttachments && displayCount > 0 && !isClearedEvent ? (
            <AttachmentsHeaderActions
                onUploadClick={handleUploadClick}
                isUploadDisabled={isMaxReached}
                sortOption={sortOption}
                onSortChange={handleSortChange}
            />
        ) : null;

    return (
        <EventPanelTab
            title={`Attachments${displayCount > 0 ? ` (${displayCount})` : ''}`}
            titleTrailingNode={titleTrailingNode}
        >
            <TabBody constrainToParentHeight={true}>
                <AttachmentsView
                    ref={attachmentsViewRef}
                    entityId={entityId}
                    entityType="CAD_AGENCY_EVENT"
                    entityNumber={eventMetadata?.agencyEventNumber}
                    onOpenPreview={(attachmentId) =>
                        openAttachmentsPanel({
                            entityId,
                            entityType: 'CAD_AGENCY_EVENT',
                            shouldFocus: true,
                            attachmentId,
                            sortOption,
                            isClearedEvent,
                        })
                    }
                    onAttachmentCountChange={handleAttachmentCountChange}
                    sortOption={sortOption}
                    isClearedEvent={isClearedEvent}
                />
            </TabBody>
        </EventPanelTab>
    );
});
